// Tags do Kanban (/TagsKanban) — cada tag é uma coluna do board.
// Mockup: "Tags do Kanban (/TagsKanban) — colunas do board".
//
// A tela existe separada de /tags porque aqui a tag é COLUNA: importa a
// ordem (arrastável) e quantos tickets estão em cada uma. Em /tags a mesma
// tag aparece na lista geral, sem esses dois recortes.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { makeStyles, alpha } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import TagModal from "../../components/TagModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import { AEROS } from "../../styles/aerosTokens";
import {
  larguraPrevia,
  payloadOrdem,
  reordena,
  rotuloTickets,
} from "./kanbanTagRules";

const GRID = "36px minmax(0,1fr) 110px 90px 110px";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    rowHead: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "10px 16px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${border}`,
    },
    row: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "11px 16px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      alignItems: "center",
      fontSize: 13,
      backgroundColor: theme.palette.aeros.surface,
    },
    rowDragging: {
      borderRadius: 12,
      border: `1px solid ${alpha(AEROS.defaultPrimary, 0.5)}`,
      boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
    },
    handle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.aeros.textTertiary,
      cursor: "grab",
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    nomeCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    swatch: { width: 12, height: 12, borderRadius: 4, flex: "none" },
    nome: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    num: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    ordem: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.secondary,
    },
    acoes: { display: "flex", gap: 2, justifyContent: "flex-end" },
    vazio: {
      padding: "28px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },

    // ---- prévia do board ----
    previaCard: {
      marginTop: 16,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: "16px 18px",
    },
    previaTitulo: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: 12,
    },
    previaColunas: { display: "flex", gap: 8, alignItems: "stretch" },
    previaColuna: {
      borderRadius: 10,
      padding: "10px 12px",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 4,
      border: `1px solid ${border}`,
    },
    previaNome: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    previaTickets: {
      fontSize: 16,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
  };
});

const TagsKanban = () => {
  const c = useCampaignPageStyles();
  const classes = useStyles();
  const history = useHistory();

  const [tags, setTags] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState(null);
  const [excluindo, setExcluindo] = useState(null);

  const carrega = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/tag/kanban");
      setTags(Array.isArray(data?.lista) ? data.lista : []);
    } catch (err) {
      toastError(err);
      setTags([]);
    }
    setCarregando(false);
  }, []);

  useEffect(() => {
    carrega();
  }, [carrega]);

  const handleDragEnd = async (resultado) => {
    if (!resultado.destination) return;
    const proximas = reordena(
      tags,
      resultado.source.index,
      resultado.destination.index
    );
    if (proximas === tags) return;

    // Otimista: a lista já aparece na ordem nova; se o servidor recusar,
    // recarrega e volta ao que ele tem.
    setTags(proximas);
    try {
      await api.put("/tags/order", { ids: payloadOrdem(proximas) });
    } catch (err) {
      toastError(err);
      carrega();
    }
  };

  const handleExcluir = async () => {
    if (!excluindo) return;
    try {
      await api.delete(`/tags/${excluindo.id}`);
      toast.success(i18n.t("aeros.kanbanTags.deleted"));
      await carrega();
    } catch (err) {
      toastError(err);
    }
    setExcluindo(null);
  };

  const previa = useMemo(() => tags.slice(0, 8), [tags]);

  return (
    <MainContainer>
      <TagModal
        open={modalAberto}
        onClose={() => {
          setModalAberto(false);
          setEmEdicao(null);
          carrega();
        }}
        tagId={emEdicao?.id}
        kanban={1}
      />
      <AerosConfirmModal
        open={Boolean(excluindo)}
        onClose={() => setExcluindo(null)}
        onConfirm={handleExcluir}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("aeros.kanbanTags.confirmTitle", {
          name: excluindo?.name || "",
        })}
        description={i18n.t("aeros.kanbanTags.confirmMessage")}
        confirmLabel={i18n.t("aeros.kanbanTags.delete")}
      />

      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("aeros.kanbanTags.title")}{" "}
            <span className={c.pageTitleCount}>· {tags.length}</span>
          </div>
          <div className={classes.headerSubtitle}>
            {i18n.t("aeros.kanbanTags.subtitle")}
          </div>
        </div>
        <div className={c.headerActions}>
          <Button
            className={c.outlineButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/kanban")}
          >
            {i18n.t("aeros.kanbanTags.backToBoard")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setEmEdicao(null);
              setModalAberto(true);
            }}
          >
            {i18n.t("aeros.kanbanTags.newTag")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody}>
        <div className={classes.card}>
          <div className={classes.rowHead}>
            <div />
            <div>{i18n.t("aeros.kanbanTags.name")}</div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.kanbanTags.ticketsColumn")}
            </div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.kanbanTags.order")}
            </div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.kanbanTags.actions")}
            </div>
          </div>

          {carregando ? (
            <div className={classes.vazio}>{i18n.t("aeros.kanbanTags.loading")}</div>
          ) : tags.length === 0 ? (
            <div className={classes.vazio}>{i18n.t("aeros.kanbanTags.empty")}</div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="tags-kanban">
                {(dropProvided) => (
                  <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                    {tags.map((tag, indice) => (
                      <Draggable
                        key={tag.id}
                        draggableId={String(tag.id)}
                        index={indice}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`${classes.row} ${
                              snapshot.isDragging ? classes.rowDragging : ""
                            }`}
                            style={dragProvided.draggableProps.style}
                          >
                            <span
                              className={classes.handle}
                              {...dragProvided.dragHandleProps}
                              aria-label={i18n.t("aeros.kanbanTags.dragHandle")}
                            >
                              <DragIndicatorIcon />
                            </span>

                            <div className={classes.nomeCell}>
                              <span
                                className={classes.swatch}
                                style={{ backgroundColor: tag.color }}
                              />
                              <span className={classes.nome} title={tag.name}>
                                {tag.name}
                              </span>
                            </div>

                            <div className={classes.num}>
                              {Number(tag.ticketsCount) || 0}
                            </div>
                            <div className={classes.ordem}>{indice + 1}</div>

                            <div className={classes.acoes}>
                              <Tooltip title={i18n.t("aeros.kanbanTags.edit")}>
                                <IconButton
                                  className={c.rowActionButton}
                                  aria-label={i18n.t("aeros.kanbanTags.edit")}
                                  onClick={() => {
                                    setEmEdicao(tag);
                                    setModalAberto(true);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={i18n.t("aeros.kanbanTags.delete")}>
                                <IconButton
                                  className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                                  aria-label={i18n.t("aeros.kanbanTags.delete")}
                                  onClick={() => setExcluindo(tag)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>

        {tags.length > 0 && (
          <div className={classes.previaCard}>
            <div className={classes.previaTitulo}>
              {i18n.t("aeros.kanbanTags.boardPreview")}
            </div>
            <div className={classes.previaColunas}>
              {previa.map((tag) => (
                <div
                  className={classes.previaColuna}
                  key={tag.id}
                  style={{
                    flex: `1 1 ${larguraPrevia(tag, previa)}%`,
                    backgroundColor: alpha(tag.color || AEROS.cyan, 0.1),
                    borderColor: alpha(tag.color || AEROS.cyan, 0.35),
                  }}
                  title={`${tag.name} · ${rotuloTickets(tag.ticketsCount)}`}
                >
                  <div className={classes.previaNome}>{tag.name}</div>
                  <div className={classes.previaTickets}>
                    {Number(tag.ticketsCount) || 0}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default TagsKanban;
